<script setup lang="ts">
import { computed, defineAsyncComponent, ref, watch } from 'vue'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import { VModal } from '@/shared/ui'
import {
  DateFieldList,
  FileFieldList,
  InteratorFieldList,
  ListFieldList,
  NumberFieldList,
  OptionalFieldList,
  ReferenceFieldList,
  TextFieldList,
} from './../model/types.ts'
import InventNotTypeFieldSettings from './settings/InventNotTypeFieldSettings.vue'
import InventTextTypeFieldSettings from './settings/InventTextTypeFieldSettings.vue'
import InventNumberTypeFieldSettings from './settings/InventNumberTypeFieldSettings.vue'
import InventDateTypeFieldSettings from './settings/InventDateTypeFieldSettings.vue'
import InventListTypeFieldSettings from './settings/InventListTypeFieldSettings.vue'
import InventTypeListTypeFieldSettings from './settings/InventTypeListTypeFieldSettings.vue'
import InventInteratorTypeFieldSetting from './settings/InventInteratorTypeFieldSetting.vue'
import InventComputedTypeFieldSettings from './settings/InventComputedTypeFieldSettings.vue'
import InventFileTypeFieldSettings from '@/entities/Inventory/TypeField/ui/settings/InventFileTypeFieldSettings.vue'
import InventAddressTypeFieldSettings from '@/entities/Inventory/TypeField/ui/settings/InventAddressTypeFieldSettings.vue'

const props = defineProps({
  groups: {
    type: Array,
    default: () => [],
  },
  /** When true (e.g. passport builder summary), computed settings hide list/field — fixed by context. */
  hideComputedListFieldSelectors: {
    type: Boolean,
    default: false,
  },
  /** When true, hide «Обязательное поле» — already toggled on the field card. */
  hideMandatory: {
    type: Boolean,
    default: false,
  },
})

const open = defineModel()
const field = defineModel<ObjectInventoryField>('field', { required: true })

const dataField = ref<ObjectInventoryField | null>(null)

/** Валидность конфигурации поля (только числовые настройки эмитят состояние). */
const isValid = ref(true)

/** Сбрасываем валидность при каждом открытии модалки. */
watch(open, (isOpen) => {
  if (isOpen) isValid.value = true
})

const setting = computed(() => {
  switch (field.value?.type) {
    case TextFieldList.STRING:
    case TextFieldList.PHONE:
    case TextFieldList.EMAIL:
    case TextFieldList.LINK:
      return InventTextTypeFieldSettings
    case TextFieldList.ADDRESS:
      return InventAddressTypeFieldSettings
    case NumberFieldList.NUMBER:
    case NumberFieldList.PERCENT:
      return InventNumberTypeFieldSettings
    case DateFieldList.DATE:
    case DateFieldList.RANGE:
    case DateFieldList.PERIOD:
      return InventDateTypeFieldSettings
    case ListFieldList.LIST:
      return InventListTypeFieldSettings
    case ListFieldList.LIST_TYPES:
      return InventTypeListTypeFieldSettings
    case InteratorFieldList.SIMPLE:
      return InventInteratorTypeFieldSetting
    case OptionalFieldList.COMPUTED:
      return InventComputedTypeFieldSettings
    case FileFieldList.LINK_FILE:
      return InventFileTypeFieldSettings
    case ReferenceFieldList.OBJECT_LINK:
      return defineAsyncComponent(() => import('./settings/InventObjectLinkTypeFieldSettings.vue'))
    default:
      return InventNotTypeFieldSettings
  }
})

const updateHandler = (data: ObjectInventoryField) => {
  if (props.hideMandatory) {
    dataField.value = { ...data, mandatory: field.value?.mandatory }
    return
  }
  dataField.value = data
}

const extraSettingProps = computed(() => {
  if (
    field.value?.type === OptionalFieldList.COMPUTED &&
    props.hideComputedListFieldSelectors
  ) {
    return { hideListAndFieldSelectors: true }
  }
  return {}
})

const validationHandler = (valid: boolean) => {
  isValid.value = valid
}

const saveHandler = () => {
  if (!isValid.value) return
  if (dataField.value) {
    const prevMandatory = field.value?.mandatory
    Object.assign(field.value, dataField.value)
    if (props.hideMandatory) {
      field.value.mandatory = prevMandatory
    }
  }
  open.value = false
}
</script>

<template>
  <VModal :title="$t('itf.field_settings')" v-model="open">
    <div class="mb-4 mt-2">
      <component
        :is="setting"
        :field="field"
        :groups="groups"
        :hide-mandatory="hideMandatory"
        v-bind="extraSettingProps"
        @update="updateHandler"
        @validation="validationHandler"
      />
    </div>

    <div class="form__btn_right">
      <VBtn :disabled="!isValid" @click="saveHandler">{{ $t('itf.save') }}</VBtn>
      <VBtn color="default" @click="open = false">{{ $t('itf.cancel') }}</VBtn>
    </div>
  </VModal>
</template>
